import {
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router';
import { RootLayout } from './RootLayout';
import { NotFoundRoute } from './NotFoundRoute';
import { AppLayout } from './AppLayout';
import { IndexRoute } from '../routes/Index';
import { LoginRoute } from '../features/auth/LoginRoute';
import { AppHomeRoute } from '../features/app/AppHomeRoute';
import { WarehousesRoute } from '../features/warehouses/WarehousesRoute';
import { ItemsRoute } from '../features/items/ItemsRoute';
import { LocationsRoute } from '../features/locations/LocationsRoute';
import { InventoryRoute } from '../features/inventory/InventoryRoute';
import { AdjustRoute } from '../features/inventory/AdjustRoute';
import { MoveRoute } from '../features/inventory/MoveRoute';
import { ProductionReceiptRoute } from '../features/inventory/ProductionReceiptRoute';
import { LicensePlatesRoute } from '../features/licensePlates/LicensePlatesRoute';
import { TransactionCodesRoute } from '../features/transactionCodes/TransactionCodesRoute';
import { CustomersRoute } from '../features/customers/CustomersRoute';
import { SalesOrdersRoute } from '../features/salesOrders/SalesOrdersRoute';
import { SalesOrderDetailRoute } from '../features/salesOrders/SalesOrderDetailRoute';
import { LotsRoute } from '../features/lots/LotsRoute';
import { LotTraceRoute } from '../features/lots/LotTraceRoute';
import { SerialsRoute } from '../features/serials/SerialsRoute';
import { SerialDetailRoute } from '../features/serials/SerialDetailRoute';
import { PicksRoute } from '../features/picks/PicksRoute';
import { PickWavesRoute } from '../features/picks/PickWavesRoute';
import { PickWaveDetailRoute } from '../features/picks/PickWaveDetailRoute';
import { DirectedPickRoute } from '../features/picks/DirectedPickRoute';
import { PickFacesRoute } from '../features/picks/PickFacesRoute';
import { PackingListRoute } from '../features/picks/PackingListRoute';
import { SuppliersRoute } from '../features/suppliers/SuppliersRoute';
import { PaymentTermsRoute } from '../features/paymentTerms/PaymentTermsRoute';
import { ShipMethodsRoute } from '../features/shipMethods/ShipMethodsRoute';
import { PlanningRoute } from '../features/planning/PlanningRoute';
import { PurchaseOrdersRoute } from '../features/purchaseOrders/PurchaseOrdersRoute';
import { PurchaseOrderDetailRoute } from '../features/purchaseOrders/PurchaseOrderDetailRoute';
import { ShipTosRoute } from '../features/purchaseOrders/ShipTosRoute';
import { ReceiptsRoute } from '../features/receipts/ReceiptsRoute';
import { UsersRoute } from '../features/users/UsersRoute';
import { GroupsRoute } from '../features/users/GroupsRoute';
import { ReceiptDetailRoute } from '../features/receipts/ReceiptDetailRoute';
import { StartRoute as RegistrationStart } from '../features/registration/StartRoute';
import { SentRoute as RegistrationSent } from '../features/registration/SentRoute';
import { CompleteRoute as RegistrationComplete } from '../features/registration/CompleteRoute';
import { AcceptRoute as InvitationAccept } from '../features/invitations/AcceptRoute';

/**
 * Router root. Public flows (login, registration) sit at the top level; the
 * authenticated app lives under /app behind RequireAuth + the App-Mode shell:
 *
 *   /                               → IndexRoute (redirects by session state)
 *   /login                          → LoginRoute (credentials + MFA challenge)
 *   /register…                      → self-registration flow
 *   /invitations/$token             → AcceptRoute (a colleague setting their password)
 *   /app                            → AppHomeRoute (dashboard)   ┐ inside the
 *   /app/warehouses                 → WarehousesRoute            │ App shell
 *   /app/items                      → ItemsRoute                 │
 *   /app/locations                  → LocationsRoute             │
 *   /app/inventory                  → InventoryRoute             │
 *   /app/inventory/adjust           → AdjustRoute                │
 *   /app/inventory/move             → MoveRoute                  │
 *   /app/inventory/production       → ProductionReceiptRoute     │
 *   /app/license-plates             → LicensePlatesRoute         │
 *   /app/sales-orders               → SalesOrdersRoute           │
 *   /app/sales-orders/$id           → SalesOrderDetailRoute      │
 *   /app/ship-methods               → ShipMethodsRoute          │
 *   /app/lots                       → LotsRoute                 │
 *   /app/lots/$id/trace             → LotTraceRoute             │
 *   /app/serials                    → SerialsRoute              │
 *   /app/serials/$id                → SerialDetailRoute         │
 *   /app/picks                      → PicksRoute                │
 *   /app/pick-waves                 → PickWavesRoute            │
 *   /app/pick-waves/$id             → PickWaveDetailRoute       │
 *   /app/picks/$id                  → DirectedPickRoute         │
 *   /app/picks/$id/packing-list     → PackingListRoute          │
 *   /app/pick-faces                 → PickFacesRoute            │
 *   /app/purchase-orders            → PurchaseOrdersRoute        │
 *   /app/purchase-orders/$id        → PurchaseOrderDetailRoute   │
 *   /app/receiving                  → ReceiptsRoute              │
 *   /app/receiving/$id              → ReceiptDetailRoute         │
 *   /app/users                      → UsersRoute                 │
 *   /app/groups                     → GroupsRoute                │
 *   /app/planning                   → PlanningRoute              ┘
 *
 * "/" is the PWA's start_url, so it is where an installed App-Mode window lands
 * on launch — it must never render a screen of its own, only route onward.
 *
 * More /app/* workflow routes land per feature module.
 */
const rootRoute = createRootRoute({
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexRoute,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginRoute,
});

const registerStartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegistrationStart,
});

interface SentSearch {
  email?: string;
  token?: string;
}

const registerSentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register/sent',
  component: RegistrationSent,
  // No token: the API stopped returning one, so there is nothing to carry and
  // nothing a crafted URL could smuggle into the sent page.
  validateSearch: (search: Record<string, unknown>): SentSearch => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
});

const registerVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register/verify/$token',
  component: RegistrationComplete,
});

// Top level and anonymous, because the invitee has no session yet — the token in the path is the
// authorization. The path is fixed by the email the server sends (TenantInvitationTemplate builds
// AppBaseUrl + "/invitations/{token}"), so it cannot be moved on this side alone.
const invitationAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitations/$token',
  component: InvitationAccept,
});

// Authenticated app section: /app (shell + RequireAuth) with nested screens.
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: AppLayout,
});

const appHomeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: AppHomeRoute,
});

const warehousesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'warehouses',
  component: WarehousesRoute,
});

const itemsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'items',
  component: ItemsRoute,
});

const locationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'locations',
  component: LocationsRoute,
});

const inventoryRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'inventory',
  component: InventoryRoute,
});

/**
 * Reached straight from the menu, because that is how the job is done: an operator walks to a rack
 * and adjusts what is in front of them. Routing them through the inquiry screen to pick an item
 * first, as the old dialog did, adds two steps to every single adjustment.
 *
 * `itemNumber` lets the inquiry screen's Adjust button hand over the item it was showing.
 */
const inventoryAdjustRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'inventory/adjust',
  component: AdjustRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    itemNumber: typeof search.itemNumber === 'string' ? search.itemNumber : undefined,
    warehouseId: typeof search.warehouseId === 'string' ? search.warehouseId : undefined,
  }),
});

/**
 * Its own screen for the same reason Adjustment is: an operator stands in an aisle and moves one
 * pallet after another. The dialog it replaces made you find the item on the inquiry screen first,
 * which added two steps to every move. Legacy's ItemMove screen is a form you sit in and repeat.
 */
const inventoryMoveRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'inventory/move',
  component: MoveRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    itemNumber: typeof search.itemNumber === 'string' ? search.itemNumber : undefined,
  }),
});

/**
 * Its own screen for the same reason Adjustment and Move are, and because the current product gives
 * production its own handheld action. The adjustment screen can post the identical transaction —
 * this one just knows the code already, so nobody picks the wrong one off a list on a four-inch
 * screen.
 */
const inventoryProductionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'inventory/production',
  component: ProductionReceiptRoute,
});

const licensePlatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'license-plates',
  component: LicensePlatesRoute,
});

const transactionCodesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'transaction-codes',
  component: TransactionCodesRoute,
});

const suppliersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'suppliers',
  component: SuppliersRoute,
});

const customersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'customers',
  component: CustomersRoute,
});

const salesOrdersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'sales-orders',
  component: SalesOrdersRoute,
});

/**
 * Same shape as the purchase order, and for the same reason: an order is a document worked on over
 * minutes and handed between people, so it needs a URL somebody can return to or send on.
 */
const salesOrderDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'sales-orders/$id',
  component: SalesOrderDetailRoute,
});

/**
 * Lots. Under Operations rather than Setup: a lot is stock, not configuration, and the person
 * opening this is asking what is on the floor and what expires next.
 */
const lotsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'lots',
  component: LotsRoute,
});

/**
 * One lot's trace. Its own address because it is the screen somebody is sent a link to during a
 * recall, when the question is which customers have the affected lot.
 */
const lotTraceRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'lots/$id/trace',
  component: LotTraceRoute,
});

/**
 * Serials. Beside Lots and under Operations for the same reason: a serial is stock, and the
 * person opening this is asking where one particular unit went.
 */
const serialsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'serials',
  component: SerialsRoute,
});

/**
 * One unit's history. Its own address because it is what a warranty claim or a field failure
 * links to: somebody has a serial number in their hand and needs to know who we sent it to.
 */
const serialDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'serials/$id',
  component: SerialDetailRoute,
});

const picksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'picks',
  component: PicksRoute,
});

/**
 * Wave picks — M5.7.
 *
 * NOT nested under /app/picks, although a wave IS a pick. The two screens answer different
 * questions: /app/picks is the floor's work queue, and this is the desk deciding what that queue
 * should contain. Burying the planning screen inside the work list would put it behind the thing
 * it produces.
 */
const pickWavesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'pick-waves',
  component: PickWavesRoute,
});

/**
 * One wave. Its own address for the same reason a pick has one: it is worked over minutes, handed
 * between people, and a supervisor needs to be able to open the one somebody is asking about.
 */
const pickWaveDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'pick-waves/$id',
  component: PickWaveDetailRoute,
});

/**
 * The directed pick, which is a handheld screen with a URL.
 *
 * A pick is walked over several minutes and can be handed to somebody else halfway through, so it
 * needs an address — the same reasoning as the purchase order and the sales order. It also means a
 * supervisor can open the route a picker is on and see where they are.
 */
const directedPickRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'picks/$id',
  component: DirectedPickRoute,
});

/**
 * The packing list for a shipment.
 *
 * Its own URL rather than a modal on the pick, because it is a DOCUMENT: somebody prints it, sends
 * the link to whoever is arguing about what was in the box, and comes back to it a week later. A
 * modal has no address and cannot be printed without the screen behind it.
 *
 * Nested under the pick because the pick is the shipment — there is no shipment table, which is
 * legacy's design and 069's.
 */
const packingListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'picks/$id/packing-list',
  component: PackingListRoute,
});

/**
 * Where each item is picked from. Setup rather than Operations: it is racking, maintained when the
 * warehouse is laid out and revisited when something moves.
 */
const pickFacesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'pick-faces',
  component: PickFacesRoute,
});

const paymentTermsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'payment-terms',
  component: PaymentTermsRoute,
});

/**
 * Carrier maintenance. Beside the payment terms and for the same reason: a small per-tenant master
 * that a form has to offer, so reading is open and only the writing is a setting.
 */
const shipMethodsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'ship-methods',
  component: ShipMethodsRoute,
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'purchase-orders',
  component: PurchaseOrdersRoute,
});

/**
 * The first $param route inside the app shell.
 *
 * A purchase order is a document worked on over days rather than a row edited in a dialog, so it
 * gets a URL somebody can return to or send to a colleague. Receiving (M4.3) will want the same.
 */
const purchaseOrderDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'purchase-orders/$id',
  component: PurchaseOrderDetailRoute,
});

const receiptsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'receiving',
  component: ReceiptsRoute,
});

/**
 * Same shape as the purchase order, and for the same reason: a delivery is keyed over an hour with
 * a line grid and its own line editor, and the URL is what somebody comes back to after walking
 * away from the desk.
 */
const receiptDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'receiving/$id',
  component: ReceiptDetailRoute,
});

const shipTosRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'ship-tos',
  component: ShipTosRoute,
});

const usersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'users',
  component: UsersRoute,
});

const groupsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'groups',
  component: GroupsRoute,
});

const planningRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: 'planning',
  component: PlanningRoute,
});

const appRoutes = appLayoutRoute.addChildren([
  appHomeRoute,
  warehousesRoute,
  itemsRoute,
  locationsRoute,
  inventoryRoute,
  inventoryAdjustRoute,
  inventoryMoveRoute,
  inventoryProductionRoute,
  licensePlatesRoute,
  transactionCodesRoute,
  suppliersRoute,
  customersRoute,
  salesOrdersRoute,
  salesOrderDetailRoute,
  lotsRoute,
  lotTraceRoute,
  serialsRoute,
  serialDetailRoute,
  picksRoute,
  pickWavesRoute,
  pickWaveDetailRoute,
  packingListRoute,
  directedPickRoute,
  pickFacesRoute,
  paymentTermsRoute,
  shipMethodsRoute,
  purchaseOrdersRoute,
  purchaseOrderDetailRoute,
  receiptsRoute,
  receiptDetailRoute,
  shipTosRoute,
  usersRoute,
  groupsRoute,
  planningRoute,
]);

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerStartRoute,
  registerSentRoute,
  registerVerifyRoute,
  invitationAcceptRoute,
  appRoutes,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Without this, TanStack Router renders the bare words "Not Found" on a white page. That is what
  // an invitee saw on 2026-08-19 when their installed PWA predated the /invitations route: the app
  // is in `prompt` update mode, so a copy keeps its own route table until somebody accepts the new
  // bundle. NotFoundRoute checks the deployed version before blaming the URL.
  defaultNotFoundComponent: NotFoundRoute,
});

// Required for TanStack Router's type-safety helpers.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
